import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { http, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      const { data } = await http.post("/auth/reset-password", { token, password });
      toast.success(data.message);
      nav("/login");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell>
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold text-[#0B132B]">Invalid reset link</h2>
          <p className="text-slate-500 text-sm mt-2">This password reset link is invalid or incomplete.</p>
          <Link to="/forgot-password"><Button className="mt-6 bg-[#0B132B] hover:bg-[#1C2541] h-11 px-8">Request a new link</Button></Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0B132B]">Set a new password</h2>
      <p className="text-slate-500 text-sm mt-1.5 mb-6">Choose a strong password for your account.</p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="password">New password</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 characters" className="mt-1.5 h-11" data-testid="reset-password-input" />
        </div>
        <div>
          <Label htmlFor="confirm">Confirm password</Label>
          <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" className="mt-1.5 h-11" data-testid="reset-confirm-input" />
        </div>
        <Button type="submit" disabled={loading} data-testid="reset-submit-button" className="w-full bg-[#0B132B] hover:bg-[#1C2541] h-11 font-semibold">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}
        </Button>
      </form>
    </AuthShell>
  );
}
